import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import {
  ICloseInventoryVendorReturnRequest,
  ICreateInventoryAdjustmentRequest,
  ICreateInventoryCounterTransactionRequest,
  ICreateInventoryCountScheduleRequest,
  ICreateInventoryStockTakeRequest,
  ICreateInventoryVendorReturnRequest,
  IInventoryAdjustmentResponse,
  IInventoryAssignmentReturnRequest,
  IInventoryControlConfigResponse,
  IInventoryCountScheduleResponse,
  IInventoryCounterTransactionResponse,
  IInventoryCustodyResponse,
  IInventoryManualHoldRequest,
  IInventoryMovementResponse,
  IInventoryReceiptQcRequest,
  IInventoryReplenishmentRunResponse,
  IInventoryReplenishmentSuggestionResponse,
  IInventoryReservationResponse,
  IInventoryStockByLocationRow,
  IInventoryStockTakeCountRequest,
  IInventoryStockTakeResponse,
  IInventoryStockValuationResponse,
  IInventoryTransferConfirmReceiptRequest,
  IInventoryVendorReturnResponse,
} from 'types/api/inventoryControlApi.interface'
import { IPaginatedResponse } from 'types/api/general.interface'
import { IInventoryControlSlice } from 'types/inventoryControlSlice.interface'
import { baseUrl } from 'utils/axios'
import { formatParams, handleErrors } from 'utils/helpers'

const initialState: IInventoryControlSlice = {
  isLoading: false,
  actionLoading: false,
  config: null,
  stockByLocation: { data: [], totalCount: 0, totalPages: 0 },
  valuation: null,
  movements: { data: [], totalCount: 0, totalPages: 0 },
  reservations: { data: [], totalCount: 0, totalPages: 0 },
  adjustments: { data: [], totalCount: 0, totalPages: 0 },
  stockTakes: { data: [], totalCount: 0, totalPages: 0 },
  singleStockTake: null,
  countSchedules: { data: [], totalCount: 0, totalPages: 0 },
  custody: { data: [], totalCount: 0, totalPages: 0 },
  vendorReturns: { data: [], totalCount: 0, totalPages: 0 },
  replenishmentSuggestions: { data: [], totalCount: 0, totalPages: 0 },
  lastCounterTransaction: null,
}

export const getInventoryControlConfig = createAsyncThunk(
  'inventoryControl/getInventoryControlConfig',
  async (_, thunkAPI) => {
    try {
      const resp: AxiosResponse<IInventoryControlConfigResponse> = await baseUrl.get(
        'inventory/control/config',
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getInventoryStockByLocation = createAsyncThunk(
  'inventoryControl/getInventoryStockByLocation',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IInventoryStockByLocationRow>> = await baseUrl.get(
        'inventory/stock/by-location',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getInventoryStockValuation = createAsyncThunk(
  'inventoryControl/getInventoryStockValuation',
  async (_, thunkAPI) => {
    try {
      const resp: AxiosResponse<IInventoryStockValuationResponse> = await baseUrl.get(
        'inventory/stock/valuation',
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getInventoryMovements = createAsyncThunk(
  'inventoryControl/getInventoryMovements',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IInventoryMovementResponse>> = await baseUrl.get(
        'inventory/movement',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getInventoryReservations = createAsyncThunk(
  'inventoryControl/getInventoryReservations',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IInventoryReservationResponse>> =
        await baseUrl.get('inventory/reservation', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createManualInventoryHold = createAsyncThunk(
  'inventoryControl/createManualInventoryHold',
  async (body: IInventoryManualHoldRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IInventoryReservationResponse> = await baseUrl.post(
        'inventory/reservation/manual-hold',
        body,
      )
      toast.success('Manual hold created')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const releaseInventoryReservation = createAsyncThunk(
  'inventoryControl/releaseInventoryReservation',
  async (id: number, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.post(`inventory/reservation/${id}/release`)
      toast.success('Reservation released')
      return { id, data: resp.data }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getInventoryAdjustments = createAsyncThunk(
  'inventoryControl/getInventoryAdjustments',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IInventoryAdjustmentResponse>> =
        await baseUrl.get('inventory/adjustment', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createInventoryAdjustment = createAsyncThunk(
  'inventoryControl/createInventoryAdjustment',
  async (body: ICreateInventoryAdjustmentRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IInventoryAdjustmentResponse> = await baseUrl.post(
        'inventory/adjustment',
        body,
      )
      toast.success('Adjustment created')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const postInventoryAdjustment = createAsyncThunk(
  'inventoryControl/postInventoryAdjustment',
  async (id: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<IInventoryAdjustmentResponse> = await baseUrl.post(
        `inventory/adjustment/${id}/post`,
      )
      toast.success('Adjustment posted')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getInventoryStockTakes = createAsyncThunk(
  'inventoryControl/getInventoryStockTakes',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IInventoryStockTakeResponse>> = await baseUrl.get(
        'inventory/stock-take',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getInventoryStockTake = createAsyncThunk(
  'inventoryControl/getInventoryStockTake',
  async (id: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<IInventoryStockTakeResponse> = await baseUrl.get(
        `inventory/stock-take/${id}`,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createInventoryStockTake = createAsyncThunk(
  'inventoryControl/createInventoryStockTake',
  async (body: ICreateInventoryStockTakeRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IInventoryStockTakeResponse> = await baseUrl.post(
        'inventory/stock-take',
        body,
      )
      toast.success('Stock take created')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const startInventoryStockTake = createAsyncThunk(
  'inventoryControl/startInventoryStockTake',
  async (id: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<IInventoryStockTakeResponse> = await baseUrl.post(
        `inventory/stock-take/${id}/start`,
      )
      toast.success('Stock take started')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const countInventoryStockTake = createAsyncThunk(
  'inventoryControl/countInventoryStockTake',
  async ({ id, body }: { id: number; body: IInventoryStockTakeCountRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IInventoryStockTakeResponse> = await baseUrl.post(
        `inventory/stock-take/${id}/count`,
        body,
      )
      toast.success('Counts saved')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const submitInventoryStockTake = createAsyncThunk(
  'inventoryControl/submitInventoryStockTake',
  async (id: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<IInventoryStockTakeResponse> = await baseUrl.post(
        `inventory/stock-take/${id}/submit`,
      )
      toast.success('Stock take submitted')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const approveInventoryStockTake = createAsyncThunk(
  'inventoryControl/approveInventoryStockTake',
  async (id: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<IInventoryStockTakeResponse> = await baseUrl.post(
        `inventory/stock-take/${id}/approve`,
      )
      toast.success('Stock take approved')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getInventoryCountSchedules = createAsyncThunk(
  'inventoryControl/getInventoryCountSchedules',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IInventoryCountScheduleResponse>> =
        await baseUrl.get('inventory/count-schedule', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createInventoryCountSchedule = createAsyncThunk(
  'inventoryControl/createInventoryCountSchedule',
  async (body: ICreateInventoryCountScheduleRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IInventoryCountScheduleResponse> = await baseUrl.post(
        'inventory/count-schedule',
        body,
      )
      toast.success('Count schedule created')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getInventoryCustody = createAsyncThunk(
  'inventoryControl/getInventoryCustody',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IInventoryCustodyResponse>> = await baseUrl.get(
        'inventory/custody',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createInventoryCounterTransaction = createAsyncThunk(
  'inventoryControl/createInventoryCounterTransaction',
  async (body: ICreateInventoryCounterTransactionRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IInventoryCounterTransactionResponse> = await baseUrl.post(
        'inventory/counter/transaction',
        body,
      )
      toast.success('Counter transaction posted')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getInventoryVendorReturns = createAsyncThunk(
  'inventoryControl/getInventoryVendorReturns',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IInventoryVendorReturnResponse>> =
        await baseUrl.get('inventory/vendor-return', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createInventoryVendorReturn = createAsyncThunk(
  'inventoryControl/createInventoryVendorReturn',
  async (body: ICreateInventoryVendorReturnRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IInventoryVendorReturnResponse> = await baseUrl.post(
        'inventory/vendor-return',
        body,
      )
      toast.success('Vendor return drafted')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const sendInventoryVendorReturn = createAsyncThunk(
  'inventoryControl/sendInventoryVendorReturn',
  async (id: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<IInventoryVendorReturnResponse> = await baseUrl.post(
        `inventory/vendor-return/${id}/send`,
      )
      toast.success('Vendor return sent')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const closeInventoryVendorReturn = createAsyncThunk(
  'inventoryControl/closeInventoryVendorReturn',
  async ({ id, body }: { id: number; body: ICloseInventoryVendorReturnRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IInventoryVendorReturnResponse> = await baseUrl.post(
        `inventory/vendor-return/${id}/close`,
        body,
      )
      toast.success('Vendor return closed')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const runInventoryReplenishment = createAsyncThunk(
  'inventoryControl/runInventoryReplenishment',
  async (_, thunkAPI) => {
    try {
      const resp: AxiosResponse<IInventoryReplenishmentRunResponse> = await baseUrl.post(
        'inventory/replenishment/run',
      )
      toast.success('Replenishment run complete')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getInventoryReplenishmentSuggestions = createAsyncThunk(
  'inventoryControl/getInventoryReplenishmentSuggestions',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IInventoryReplenishmentSuggestionResponse>> =
        await baseUrl.get('inventory/replenishment/suggestions', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const postInventoryReceiptQc = createAsyncThunk(
  'inventoryControl/postInventoryReceiptQc',
  async ({ id, body }: { id: number; body: IInventoryReceiptQcRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.post(`inventory/receipt/${id}/qc`, body)
      toast.success('QC saved')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const returnInventoryAssignment = createAsyncThunk(
  'inventoryControl/returnInventoryAssignment',
  async ({ id, body }: { id: number; body: IInventoryAssignmentReturnRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.post(
        `inventory/assignment/request/${id}/return`,
        body,
      )
      toast.success('Assignment returned')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const confirmInventoryTransferReceipt = createAsyncThunk(
  'inventoryControl/confirmInventoryTransferReceipt',
  async (
    { id, body }: { id: number; body: IInventoryTransferConfirmReceiptRequest },
    thunkAPI,
  ) => {
    try {
      const resp: AxiosResponse = await baseUrl.post(
        `inventory/transfer/${id}/confirm-receipt`,
        body,
      )
      toast.success('Transfer receipt confirmed')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const replaceById = <T extends { id: number }>(list: T[], item: T): T[] => {
  const idx = list.findIndex((row) => row.id === item.id)
  if (idx < 0) return [item, ...list]
  const next = [...list]
  next[idx] = item
  return next
}

const inventoryControlSlice = createSlice({
  name: 'inventoryControl',
  initialState,
  reducers: {
    clearInventoryControl: () => initialState,
    clearInventoryStockTakes: (state) => {
      state.stockTakes = { data: [], totalCount: 0, totalPages: 0 }
      state.singleStockTake = null
    },
    clearInventoryReservations: (state) => {
      state.reservations = { data: [], totalCount: 0, totalPages: 0 }
    },
    clearInventoryMovements: (state) => {
      state.movements = { data: [], totalCount: 0, totalPages: 0 }
    },
    clearInventoryAdjustments: (state) => {
      state.adjustments = { data: [], totalCount: 0, totalPages: 0 }
    },
    clearInventoryCustody: (state) => {
      state.custody = { data: [], totalCount: 0, totalPages: 0 }
    },
    clearInventoryStockByLocation: (state) => {
      state.stockByLocation = { data: [], totalCount: 0, totalPages: 0 }
    },
  },
  extraReducers: {
    [getInventoryControlConfig.fulfilled.type]: (
      state,
      { payload }: { payload: IInventoryControlConfigResponse },
    ) => {
      state.config = payload
    },
    [getInventoryControlConfig.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getInventoryStockByLocation.pending.type]: (state) => {
      state.isLoading = true
    },
    [getInventoryStockByLocation.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IInventoryStockByLocationRow> },
    ) => {
      state.isLoading = false
      state.stockByLocation.data = payload.data
      state.stockByLocation.totalCount = payload.totalCount
      state.stockByLocation.totalPages = payload.totalPages
    },
    [getInventoryStockByLocation.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getInventoryStockValuation.fulfilled.type]: (
      state,
      { payload }: { payload: IInventoryStockValuationResponse },
    ) => {
      state.valuation = payload
    },
    [getInventoryStockValuation.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getInventoryMovements.pending.type]: (state) => {
      state.isLoading = true
    },
    [getInventoryMovements.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IInventoryMovementResponse> },
    ) => {
      state.isLoading = false
      state.movements.data = payload.data
      state.movements.totalCount = payload.totalCount
      state.movements.totalPages = payload.totalPages
    },
    [getInventoryMovements.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getInventoryReservations.pending.type]: (state) => {
      state.isLoading = true
    },
    [getInventoryReservations.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IInventoryReservationResponse> },
    ) => {
      state.isLoading = false
      state.reservations.data = payload.data
      state.reservations.totalCount = payload.totalCount
      state.reservations.totalPages = payload.totalPages
    },
    [getInventoryReservations.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createManualInventoryHold.pending.type]: (state) => {
      state.actionLoading = true
    },
    [createManualInventoryHold.fulfilled.type]: (
      state,
      { payload }: { payload: IInventoryReservationResponse },
    ) => {
      state.actionLoading = false
      state.reservations.data = [payload, ...state.reservations.data]
    },
    [createManualInventoryHold.rejected.type]: (state, { payload }) => {
      state.actionLoading = false
      handleErrors(payload)
    },
    [releaseInventoryReservation.fulfilled.type]: (state, { meta }) => {
      const id = meta.arg as number
      state.reservations.data = state.reservations.data.map((row) =>
        row.id === id ? { ...row, status: 3 } : row,
      )
    },
    [releaseInventoryReservation.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getInventoryAdjustments.pending.type]: (state) => {
      state.isLoading = true
    },
    [getInventoryAdjustments.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IInventoryAdjustmentResponse> },
    ) => {
      state.isLoading = false
      state.adjustments.data = payload.data
      state.adjustments.totalCount = payload.totalCount
      state.adjustments.totalPages = payload.totalPages
    },
    [getInventoryAdjustments.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createInventoryAdjustment.fulfilled.type]: (
      state,
      { payload }: { payload: IInventoryAdjustmentResponse },
    ) => {
      state.adjustments.data = [payload, ...state.adjustments.data]
    },
    [createInventoryAdjustment.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [postInventoryAdjustment.fulfilled.type]: (
      state,
      { payload }: { payload: IInventoryAdjustmentResponse },
    ) => {
      state.adjustments.data = replaceById(state.adjustments.data, payload)
    },
    [postInventoryAdjustment.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getInventoryStockTakes.pending.type]: (state) => {
      state.isLoading = true
    },
    [getInventoryStockTakes.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IInventoryStockTakeResponse> },
    ) => {
      state.isLoading = false
      state.stockTakes.data = payload.data
      state.stockTakes.totalCount = payload.totalCount
      state.stockTakes.totalPages = payload.totalPages
    },
    [getInventoryStockTakes.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getInventoryStockTake.fulfilled.type]: (
      state,
      { payload }: { payload: IInventoryStockTakeResponse },
    ) => {
      state.singleStockTake = payload
    },
    [getInventoryStockTake.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [createInventoryStockTake.fulfilled.type]: (
      state,
      { payload }: { payload: IInventoryStockTakeResponse },
    ) => {
      state.stockTakes.data = [payload, ...state.stockTakes.data]
      state.singleStockTake = payload
    },
    [createInventoryStockTake.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [startInventoryStockTake.fulfilled.type]: (
      state,
      { payload }: { payload: IInventoryStockTakeResponse },
    ) => {
      state.singleStockTake = payload
      state.stockTakes.data = replaceById(state.stockTakes.data, payload)
    },
    [startInventoryStockTake.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [countInventoryStockTake.fulfilled.type]: (
      state,
      { payload }: { payload: IInventoryStockTakeResponse },
    ) => {
      state.singleStockTake = payload
    },
    [countInventoryStockTake.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [submitInventoryStockTake.fulfilled.type]: (
      state,
      { payload }: { payload: IInventoryStockTakeResponse },
    ) => {
      state.singleStockTake = payload
      state.stockTakes.data = replaceById(state.stockTakes.data, payload)
    },
    [submitInventoryStockTake.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [approveInventoryStockTake.fulfilled.type]: (
      state,
      { payload }: { payload: IInventoryStockTakeResponse },
    ) => {
      state.singleStockTake = payload
      state.stockTakes.data = replaceById(state.stockTakes.data, payload)
    },
    [approveInventoryStockTake.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getInventoryCountSchedules.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IInventoryCountScheduleResponse> },
    ) => {
      state.countSchedules.data = payload.data
      state.countSchedules.totalCount = payload.totalCount
      state.countSchedules.totalPages = payload.totalPages
    },
    [getInventoryCountSchedules.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [createInventoryCountSchedule.fulfilled.type]: (
      state,
      { payload }: { payload: IInventoryCountScheduleResponse },
    ) => {
      state.countSchedules.data = [payload, ...state.countSchedules.data]
    },
    [createInventoryCountSchedule.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getInventoryCustody.pending.type]: (state) => {
      state.isLoading = true
    },
    [getInventoryCustody.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IInventoryCustodyResponse> },
    ) => {
      state.isLoading = false
      state.custody.data = payload.data
      state.custody.totalCount = payload.totalCount
      state.custody.totalPages = payload.totalPages
    },
    [getInventoryCustody.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createInventoryCounterTransaction.pending.type]: (state) => {
      state.actionLoading = true
    },
    [createInventoryCounterTransaction.fulfilled.type]: (
      state,
      { payload }: { payload: IInventoryCounterTransactionResponse },
    ) => {
      state.actionLoading = false
      state.lastCounterTransaction = payload
    },
    [createInventoryCounterTransaction.rejected.type]: (state, { payload }) => {
      state.actionLoading = false
      handleErrors(payload)
    },
    [getInventoryVendorReturns.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IInventoryVendorReturnResponse> },
    ) => {
      state.vendorReturns.data = payload.data
      state.vendorReturns.totalCount = payload.totalCount
      state.vendorReturns.totalPages = payload.totalPages
    },
    [getInventoryVendorReturns.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [createInventoryVendorReturn.fulfilled.type]: (
      state,
      { payload }: { payload: IInventoryVendorReturnResponse },
    ) => {
      state.vendorReturns.data = [payload, ...state.vendorReturns.data]
    },
    [createInventoryVendorReturn.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [sendInventoryVendorReturn.fulfilled.type]: (
      state,
      { payload }: { payload: IInventoryVendorReturnResponse },
    ) => {
      state.vendorReturns.data = replaceById(state.vendorReturns.data, payload)
    },
    [sendInventoryVendorReturn.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [closeInventoryVendorReturn.fulfilled.type]: (
      state,
      { payload }: { payload: IInventoryVendorReturnResponse },
    ) => {
      state.vendorReturns.data = replaceById(state.vendorReturns.data, payload)
    },
    [closeInventoryVendorReturn.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getInventoryReplenishmentSuggestions.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IInventoryReplenishmentSuggestionResponse> },
    ) => {
      state.replenishmentSuggestions.data = payload.data
      state.replenishmentSuggestions.totalCount = payload.totalCount
      state.replenishmentSuggestions.totalPages = payload.totalPages
    },
    [getInventoryReplenishmentSuggestions.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [runInventoryReplenishment.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [postInventoryReceiptQc.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [returnInventoryAssignment.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [confirmInventoryTransferReceipt.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
  },
})

export const {
  clearInventoryControl,
  clearInventoryStockTakes,
  clearInventoryReservations,
  clearInventoryMovements,
  clearInventoryAdjustments,
  clearInventoryCustody,
  clearInventoryStockByLocation,
} = inventoryControlSlice.actions

export default inventoryControlSlice.reducer
